import { Caption1, Text, makeStyles } from '@fluentui/react-components';
import { formatRecordedCalendarDate, foundUsThroughLabel, type Person } from '@vigeo/shared';
import { tokens } from '@vigeo/ui';

const useStyles = makeStyles({
  panel: {
    display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS, minWidth: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    padding: tokens.spacingHorizontalL,
  },
  row: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, minWidth: 0 },
});

/**
 * How this candidacy started (#258): the interview it begins after, who was in it, and where
 * they came from — captured once by the Add candidate form.
 *
 * NAMES, FROM IDS WHERE THERE ARE IDS. Julio asked for interviewers as people rather than free
 * text so an interview can be linked to an Outlook account later. This renders the stored name
 * either way; the id is what a directory will match on when one exists, and is deliberately not
 * shown — an Entra object id means nothing to HR.
 *
 * Renders nothing at all when the person has no intake, rather than a card of dashes: every
 * person recorded before this existed has none, and so does anyone added by CSV import.
 */
export function CandidateIntakeCard({ person }: { readonly person: Person }) {
  const styles = useStyles();
  const intake = person.intake;
  if (intake === undefined) return null;
  const { interviewedOn, interviewers, foundUsThrough } = intake;
  if (interviewedOn === null && interviewers.length === 0 && foundUsThrough === null) return null;
  /**
   * THE LABEL, NEVER THE STORED ID (#341). A source is stored as `indeed` so it can be counted;
   * "Found us through indeed" is what HR would have read if this rendered the value. "Other" reads
   * as what HR typed, and a value stored before the list existed is free text and is shown as it
   * was written.
   */
  const source = foundUsThroughLabel(intake);

  return (
    <section className={styles.panel} aria-label="How they came to us">
      <div>
        <Text weight="semibold">How they came to us</Text><br />
        <Caption1>Recorded when this candidate was added, after their interview.</Caption1>
      </div>
      {interviewedOn === null ? null : (
        <div className={styles.row}>
          <Caption1>Interviewed on</Caption1>
          <Text>{formatRecordedCalendarDate(interviewedOn)}</Text>
        </div>
      )}
      {interviewers.length === 0 ? null : (
        <div className={styles.row}>
          <Caption1>Interviewed by</Caption1>
          <Text>{interviewers.map((person) => person.name).join(', ')}</Text>
        </div>
      )}
      {source === null ? null : (
        <div className={styles.row}>
          <Caption1>Found us through</Caption1>
          <Text>{source}</Text>
        </div>
      )}
    </section>
  );
}
